import { Link } from 'react-router-dom';
import { DollarSign, Building2, Shield, Crown, AlertTriangle, Ban, Clock, Mail, GraduationCap } from 'lucide-react';
import SEO from '../components/shared/SEO';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="refund-policy-page">
      <SEO 
        title="Refund Policy | Queen B Logistics"
        description="Refund Policy for Queen B Holdings LLC, DBA Queen B Logistics - Understand our refund terms for Build, Protect, and Grow services."
      />
      
      {/* Header */}
      <section className="py-16 bg-[#050505] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Refund Policy
          </h1>
          <p className="text-white/60">
            Queen B Holdings LLC, DBA Queen B Logistics<br />
            <span className="text-white/40">Last Updated: February 2026</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            
            <PolicySection number="1" title="Overview">
              <p>Queen B Logistics operates under a structured service model consisting of:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                <div className="bg-[#CD7F32]/10 border border-[#CD7F32]/30 p-4 text-center">
                  <Building2 className="w-6 h-6 text-[#CD7F32] mx-auto mb-2" />
                  <p className="text-[#CD7F32] font-bold m-0 text-sm">BUILD</p>
                  <p className="text-white/50 text-xs m-0">Signature Program – one-time services</p>
                </div>
                <div className="bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 p-4 text-center">
                  <Shield className="w-6 h-6 text-[#C0C0C0] mx-auto mb-2" />
                  <p className="text-[#C0C0C0] font-bold m-0 text-sm">PROTECT</p>
                  <p className="text-white/50 text-xs m-0">Compliance Monitoring – subscription</p>
                </div>
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 text-center">
                  <Crown className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                  <p className="text-[#D4AF37] font-bold m-0 text-sm">GROW</p>
                  <p className="text-white/50 text-xs m-0">Dispatch Services – subscription</p>
                </div>
              </div>
              <p>Because our services involve administrative labor, regulatory processing, and operational setup, refund eligibility depends on service type and stage of work.</p>
            </PolicySection>

            <PolicySection number="2" title="Signature Program (Build Services) – One-Time Fees" icon={Building2}>
              <div className="bg-red-500/10 border border-red-500/30 p-4 my-4">
                <p className="text-red-400 m-0 font-bold">Signature Program fees are non-refundable once work has begun.</p>
              </div>
              <p>Work is considered "begun" when any of the following occur:</p>
              <ul>
                <li>Intake review is completed</li>
                <li>Formation documents are prepared or filed</li>
                <li>Authority filing support has started</li>
                <li>Administrative work has been performed</li>
              </ul>
              <p className="text-white/50">If payment is made but the application is denied before any work begins, a refund may be issued at the company's discretion minus administrative processing fees.</p>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0"><strong>We do not guarantee authority approval</strong>, as approval decisions are made by government agencies (FMCSA and related entities).</p>
              </div>
            </PolicySection>

            <PolicySection number="3" title="Compliance Monitoring (Protect – Subscription)" icon={Shield}>
              <p><strong>Subscription payments are non-refundable once the billing cycle has started.</strong></p>
              <p>If cancellation occurs mid-cycle:</p>
              <ul>
                <li>Service remains active until the end of the current billing period</li>
                <li>No prorated refunds are issued</li>
              </ul>
            </PolicySection>

            <PolicySection number="4" title="Dispatch Services (Grow – Subscription)" icon={Crown}>
              <p>Dispatch services involve active operational labor and broker negotiations.</p>
              <p><strong>Monthly dispatch fees are non-refundable once dispatch activity has begun for that billing cycle.</strong></p>
              <p className="text-white/50">If no dispatch activity has begun and cancellation occurs before work starts, refund requests may be reviewed on a case-by-case basis.</p>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0">Revenue performance is dependent on market conditions, broker relationships, and carrier operations. <strong>No revenue guarantees are provided.</strong></p>
              </div>
            </PolicySection>

            <PolicySection number="5" title="Queen B Academy (Educational Courses)" icon={GraduationCap}>
              <div className="bg-red-500/10 border border-red-500/30 p-4 my-4">
                <p className="text-red-400 m-0 font-bold">All Queen B Academy course purchases are final and non-refundable.</p>
              </div>
              <p>Queen B Academy provides digital educational content including:</p>
              <ul>
                <li>CDL Mastery 101</li>
                <li>Dispatcher 101</li>
                <li>Trucking 101</li>
                <li>Freight Broker 101</li>
              </ul>
              <p>Due to the immediate digital delivery nature of our courses:</p>
              <ul>
                <li>Access is granted instantly upon purchase</li>
                <li>Course content, quizzes, and resources become immediately available</li>
                <li>No refunds will be issued once payment is processed</li>
                <li>Bundle purchases are also non-refundable</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0"><strong>We recommend reviewing course descriptions carefully before purchase.</strong> Preview content is available on our Academy page.</p>
              </div>
            </PolicySection>

            <PolicySection number="6" title="Denied Service Applications">
              <p>If a carrier is not approved during onboarding due to compliance issues, misrepresentation, or capacity limitations:</p>
              <ul>
                <li>A refund may be issued minus administrative review costs</li>
                <li>Queen B Logistics reserves the right to deny service</li>
              </ul>
            </PolicySection>

            <PolicySection number="7" title="Chargebacks & Disputes" icon={AlertTriangle}>
              <p>Initiating a chargeback without first contacting our support team may result in:</p>
              <ul>
                <li>Immediate account suspension</li>
                <li>Termination of services</li>
                <li>Legal recovery efforts where applicable</li>
              </ul>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 my-4 flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <p className="text-white m-0">We encourage clients to contact <a href="mailto:support@queenblogistics.com" className="text-[#D4AF37] hover:underline">support@queenblogistics.com</a> before initiating disputes.</p>
              </div>
            </PolicySection>

            <PolicySection number="8" title="Processing Time" icon={Clock}>
              <p>Approved refunds (if applicable) may take <strong>5–10 business days</strong> depending on payment processor timelines.</p>
            </PolicySection>

          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="text-[#D4AF37] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const PolicySection = ({ number, title, icon: Icon, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold text-sm">
        {number}
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase m-0">
        {title}
      </h2>
      {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
    </div>
    <div className="text-white/70 leading-relaxed pl-11">
      {children}
    </div>
  </div>
);

export default RefundPolicyPage;
